import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccessTime as AccessTimeIcon,
  Description as DocumentIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  HeadsetMic as HeadsetIcon,
  Note as NoteIcon,
  OpenInNew as OpenInNewIcon,
  Replay as ReopenIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { atendimentoService, AtendimentoMensagem, AtendimentoTicket } from '../../../services/atendimentoService';
import tagService from '../../../services/tagService';
import { Tag } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { getMensagemRemetenteLabel } from '../../../utils/usuarioUtils';
import AtendimentoMensagemConteudo from './AtendimentoMensagemConteudo';
import {
  buildReactionsByWid,
  getQuotedMessagePreviewText,
  getQuotedWidFromMessage,
  ReactionMessage,
} from '../utils/mensagemConteudoUtils';

interface VisualizarTicketDialogProps {
  open: boolean;
  ticket: AtendimentoTicket | null;
  onClose: () => void;
  onTicketReopened?: (ticket: AtendimentoTicket) => void;
}

const getInitials = (name?: string | null) => name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';

const formatDateTime = (value?: string | null) => value ? new Date(value).toLocaleString('pt-BR') : '-';

const formatStatusLabel = (status?: string | null) => status ? status.replace(/_/g, ' ') : '-';

const formatMsgTime = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const getTicketStatusLabel = (status?: AtendimentoTicket['status'] | null) => {
  switch (status) {
    case 'em_atendimento':
      return 'Em atendimento';
    case 'pendente':
      return 'Aguardando';
    case 'fechado':
      return 'Fechado';
    case 'fechado_bot':
      return 'Fechado (Bot)';
    default:
      return status || 'Atendimento';
  }
};

const formatTicketDate = (value?: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AckIcon = ({ ack }: { ack?: string | null }) => {
  if (!ack) return <AccessTimeIcon sx={{ fontSize: 15, opacity: 0.65 }} />;
  switch (ack) {
    case 'error':
      return <ErrorOutlineIcon color="error" sx={{ fontSize: 15 }} />;
    case 'pending':
      return <AccessTimeIcon sx={{ fontSize: 15, opacity: 0.65 }} />;
    case 'sent':
      return <DoneIcon sx={{ fontSize: 15, opacity: 0.65 }} />;
    case 'received':
      return <DoneAllIcon sx={{ fontSize: 15, opacity: 0.65 }} />;
    case 'read':
    case 'played':
      return <DoneAllIcon color="info" sx={{ fontSize: 15 }} />;
    default:
      return <AccessTimeIcon sx={{ fontSize: 15, opacity: 0.65 }} />;
  }
};

const MensagemBolha: React.FC<{
  msg: AtendimentoMensagem;
  mensagens: AtendimentoMensagem[];
  contatoNome: string;
  remetenteLabel: string;
  attachedReactions: ReactionMessage[];
  currentUserId?: number | null;
  usuariosPorId: Map<number, { nome?: string; nome_resumido?: string | null }>;
}> = ({ msg, mensagens, contatoNome, remetenteLabel, attachedReactions, currentUserId, usuariosPorId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMine = msg.de_mim;
  const isPrivate = msg.tipo === 'private';
  const isStickerMessage = msg.tipo === 'sticker';
  const quotedWid = getQuotedWidFromMessage(msg);
  const quotedMessage = quotedWid
    ? mensagens.find((item) => item.wid === quotedWid)
    : msg.citacao_id
      ? mensagens.find((item) => item.id === msg.citacao_id)
      : null;
  const hasQuotedMessage = Boolean(quotedWid || msg.citacao_id || quotedMessage);

  const bubbleBg = isPrivate
    ? isDark ? 'rgba(255,193,7,0.12)' : '#fff8e1'
    : isMine
      ? theme.palette.primary.main
      : isDark ? 'rgba(255,255,255,0.08)' : '#fff';

  const bubbleShadow = theme.shadows[1];

  return (
    <Box display="flex" justifyContent={isPrivate || isMine ? 'flex-end' : 'flex-start'} sx={{ mb: 1 }}>
      <Box
        sx={{
          position: 'relative',
          maxWidth: isPrivate ? { xs: '94%', sm: '78%' } : { xs: '88%', sm: '72%' },
          bgcolor: isStickerMessage ? 'transparent' : bubbleBg,
          color: isMine && !isPrivate ? '#fff' : 'text.primary',
          border: isStickerMessage ? 'none' : isPrivate ? '1px dashed #f9a825' : `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.divider}`,
          borderLeft: isStickerMessage ? 'none' : isPrivate ? '4px solid #f9a825' : undefined,
          borderRadius: isPrivate ? 1.5 : isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          px: isStickerMessage ? 0.4 : 1.25,
          py: isStickerMessage ? 0.35 : 0.85,
          boxShadow: isStickerMessage ? 'none' : bubbleShadow,
        }}
      >
        {attachedReactions.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -12,
              [isMine || isPrivate ? 'right' : 'left']: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 0.2,
              px: 0.55,
              py: 0.1,
              minHeight: 22,
              borderRadius: 999,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: bubbleShadow,
              zIndex: 2,
              fontSize: '0.9rem',
              lineHeight: 1,
            }}
          >
            {attachedReactions.slice(-3).map((item, index) => (
              <Box component="span" key={`${item.quotedWid}-${index}`}>
                {item.emoji}
              </Box>
            ))}
          </Box>
        )}

        {isPrivate ? (
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <NoteIcon sx={{ fontSize: 14, color: '#f9a825' }} />
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>
              Nota privada
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 800, mb: 0.5, color: isMine && !isPrivate ? 'inherit' : 'primary.main' }}
          >
            {remetenteLabel}:
          </Typography>
        )}

        {hasQuotedMessage && (
          <Box
            sx={{
              mb: 0.75,
              px: 1,
              py: 0.6,
              borderRadius: 1,
              bgcolor: alpha(isMine ? theme.palette.success.dark : theme.palette.primary.main, isDark ? 0.2 : 0.1),
              borderLeft: `3px solid ${isMine ? theme.palette.success.dark : theme.palette.primary.main}`,
              maxWidth: { xs: 230, sm: 310 },
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.66rem', fontWeight: 700, color: isMine ? theme.palette.success.dark : theme.palette.primary.main }}>
              {quotedMessage ? getMensagemRemetenteLabel(quotedMessage, contatoNome, currentUserId, usuariosPorId) : contatoNome}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.7rem' }}>
              {getQuotedMessagePreviewText(msg, quotedMessage || null)}
            </Typography>
          </Box>
        )}

        {msg.deletada && (
          <Alert severity="warning" sx={{ py: 0, mb: 0.75, fontSize: '0.72rem' }}>
            Mensagem apagada no WhatsApp
          </Alert>
        )}

        <AtendimentoMensagemConteudo
          msg={msg}
          isMine={isMine}
          isPrivate={isPrivate}
          onOpenImage={({ src }) => window.open(src, '_blank', 'noopener,noreferrer')}
        />

        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.4} mt={0.55} mb={attachedReactions.length > 0 ? 1.15 : 0}>
          {msg.editada && !msg.deletada && (
            <Typography variant="caption" sx={{ fontSize: '0.62rem', opacity: 0.75 }}>editada</Typography>
          )}
          <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.75 }}>
            {formatMsgTime(msg.timestamp)}
          </Typography>
          {isMine && !isPrivate && (
            <AckIcon ack={msg.ack} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const VisualizarTicketDialog: React.FC<VisualizarTicketDialogProps> = ({ open, ticket, onClose, onTicketReopened }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const endRef = useRef<HTMLDivElement | null>(null);

  const handleAbrirNaPagina = () => {
    onClose();
    if (ticket) {
      navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
    }
  };
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(false);
  const [ticketAtual, setTicketAtual] = useState<AtendimentoTicket | null>(ticket);
  const [mensagens, setMensagens] = useState<AtendimentoMensagem[]>([]);
  const [loading, setLoading] = useState(false);
  const [reabrindo, setReabrindo] = useState(false);
  const [erro, setErro] = useState('');
  const [temHistoricoAnterior, setTemHistoricoAnterior] = useState(false);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [ticketMaisAntigoId, setTicketMaisAntigoId] = useState<number>(ticket?.id || 0);
  const [ticketsCarregados, setTicketsCarregados] = useState<Map<number, AtendimentoTicket>>(new Map());
  const [allTags, setAllTags] = useState<Tag[]>([]);

  useEffect(() => {
    if (open) {
      tagService.getAll({ limit: 1000 }).then((res) => {
        if (res.success && res.data) {
          setAllTags(res.data);
        }
      }).catch(() => { });
    }
  }, [open]);

  useEffect(() => {
    if (open && ticket) {
      setTicketAtual(ticket);
      setTicketMaisAntigoId(ticket.id);
      setTemHistoricoAnterior(false);
      setTicketsCarregados(new Map([[ticket.id, ticket]]));
    }
  }, [open, ticket]);

  const ticketTags = useMemo(() => {
    if (!ticketAtual?.tags) return [];
    let rawArray: any[] = [];
    if (Array.isArray(ticketAtual.tags)) {
      rawArray = ticketAtual.tags;
    } else if (typeof ticketAtual.tags === 'string') {
      try {
        const parsed = JSON.parse(ticketAtual.tags);
        if (Array.isArray(parsed)) rawArray = parsed;
      } catch (e) {
        rawArray = [];
      }
    }
    const ids = rawArray.map((v) => typeof v === 'object' && v !== null ? Number(v.id) : Number(v)).filter((n) => !isNaN(n));
    return allTags.filter((t) => ids.includes(t.id));
  }, [ticketAtual?.tags, allTags]);

  const carregarMensagens = async (ticketId: number) => {
    setLoading(true);
    setErro('');
    setMensagens([]);
    try {
      const data = await atendimentoService.getMensagens(ticketId);
      setMensagens(data || []);
      shouldAutoScrollRef.current = true;

      const possuiAnterior = await atendimentoService.verificarHistoricoAnterior(ticketId).catch(() => false);
      setTemHistoricoAnterior(possuiAnterior);
    } catch (error: any) {
      setErro(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Erro ao carregar mensagens.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !ticketAtual) return;

    let ativo = true;
    (async () => {
      if (!ativo) return;
      await carregarMensagens(ticketAtual.id);
    })();

    return () => {
      ativo = false;
    };
  }, [open, ticketAtual?.id]);

  const carregarHistoricoAnterior = async () => {
    if (carregandoHistorico || !temHistoricoAnterior || !ticketMaisAntigoId) return;

    const container = messagesContainerRef.current;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const scrollTopBefore = container?.scrollTop ?? 0;

    try {
      setCarregandoHistorico(true);
      const data = await atendimentoService.getHistoricoAnterior(ticketMaisAntigoId);

      if (!data.ticket) {
        setTemHistoricoAnterior(false);
        return;
      }

      setTicketMaisAntigoId(data.ticket.id);
      setTemHistoricoAnterior(Boolean(data.possui_mais));
      setTicketsCarregados((prev) => {
        const next = new Map(prev);
        next.set(data.ticket!.id, data.ticket!);
        return next;
      });

      if (!data.mensagens?.length) {
        return;
      }

      setMensagens((prev) => [...data.mensagens, ...prev]);

      requestAnimationFrame(() => {
        if (!container) return;
        const scrollHeightAfter = container.scrollHeight;
        container.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Erro ao carregar mensagens anteriores.');
    } finally {
      setCarregandoHistorico(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (shouldAutoScrollRef.current) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
      shouldAutoScrollRef.current = false;
    }
  }, [open, mensagens.length]);

  useEffect(() => {
    if (open) return;
    setTicketAtual(null);
    setMensagens([]);
    setErro('');
    setReabrindo(false);
    setTemHistoricoAnterior(false);
    setCarregandoHistorico(false);
    setTicketMaisAntigoId(0);
    setTicketsCarregados(new Map());
  }, [open]);

  const handleReabrir = async () => {
    if (!ticketAtual) return;

    try {
      setReabrindo(true);
      setErro('');
      const data = await atendimentoService.reabrirTicket(ticketAtual.id);
      setTicketAtual(data);
      setTicketsCarregados((prev) => {
        const next = new Map(prev);
        next.set(data.id, data);
        return next;
      });
      onTicketReopened?.(data);
    } catch (error: any) {
      setErro(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Erro ao reabrir atendimento.');
    } finally {
      setReabrindo(false);
    }
  };

  const contatoNome = ticketAtual?.contato?.nome || 'Contato';
  const usuariosPorId = useMemo(() => {
    const map = new Map<number, { nome?: string; nome_resumido?: string | null }>();
    if (user) {
      map.set(user.id, { nome: user.nome, nome_resumido: user.nome_resumido });
    }
    mensagens.forEach((msg) => {
      if (msg.usuario_id && msg.usuario) {
        map.set(msg.usuario_id, msg.usuario);
      }
    });
    return map;
  }, [user, mensagens]);
  const reactionsByWid = useMemo(() => buildReactionsByWid(mensagens), [mensagens]);
  const inicioPorTicket = useMemo(() => {
    const map = new Map<number, number>();
    mensagens.forEach((msg, index) => {
      if (!map.has(msg.ticket_id)) map.set(msg.ticket_id, index);
    });
    return map;
  }, [mensagens]);
  const possuiMultiplosTickets = useMemo(
    () => new Set(mensagens.map((msg) => msg.ticket_id)).size > 1,
    [mensagens],
  );
  const obterTicketInfo = (ticketId: number): AtendimentoTicket | null => {
    if (ticketId === ticketAtual?.id) return ticketAtual;
    return ticketsCarregados.get(ticketId) || null;
  };
  const podeReabrir = ticketAtual?.status === 'fechado' || ticketAtual?.status === 'fechado_bot';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      }}
    >
      <DialogTitle sx={{ py: 1.25 }}>
        <Box display="flex" alignItems="center" gap={1.2}>
          <Avatar
            src={ticketAtual ? atendimentoService.getAvatarUrl(ticketAtual.contato.foto_perfil) : undefined}
            sx={{ width: 36, height: 36, bgcolor: '#26a69a', fontSize: '0.8rem' }}
          >
            {ticketAtual && !ticketAtual.contato.foto_perfil && getInitials(contatoNome)}
          </Avatar>
          <Box minWidth={0} flex={1}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              Ticket #{ticketAtual?.id} - {contatoNome}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
              <Chip label={formatStatusLabel(ticketAtual?.status)} size="small" sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }} />
              {ticketAtual?.conexao && <Chip icon={<WhatsAppIcon sx={{ fontSize: '0.78rem !important' }} />} label={ticketAtual.conexao.nome} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
              {ticketAtual?.usuario && <Typography variant="caption" color="primary.main" fontWeight={700}><HeadsetIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', mr: 0.25 }} />{ticketAtual.usuario.nome}</Typography>}
              {ticketTags.map((t) => (
                <Chip
                  key={t.id}
                  label={t.titulo}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: alpha(t.color, 0.15),
                    color: t.color,
                    border: `1px solid ${alpha(t.color, 0.3)}`,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          ref={messagesContainerRef}
          sx={{
            height: { xs: '60vh', md: '66vh' },
            overflowY: 'auto',
            p: 2,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#eef2f5',
          }}
        >
          {erro && <Alert severity="error" sx={{ mb: 1.5 }}>{erro}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : mensagens.length === 0 ? (
            <Box textAlign="center" py={6}>
              <DocumentIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Nenhuma mensagem encontrada neste ticket.</Typography>
            </Box>
          ) : (
            <>
              {temHistoricoAnterior && (
                <Box display="flex" justifyContent="center" mb={0.5}>
                  <Button
                    size="small"
                    variant="text"
                    disabled={carregandoHistorico}
                    onClick={carregarHistoricoAnterior}
                    startIcon={carregandoHistorico ? <CircularProgress size={14} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.35,
                      color: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                    }}
                  >
                    {carregandoHistorico ? 'Carregando...' : 'Ver mais'}
                  </Button>
                </Box>
              )}
              {mensagens.map((msg, index) => {
                if (msg.tipo === 'reaction') return null;

                const indiceInicioTicket = inicioPorTicket.get(msg.ticket_id);
                const mostrarSeparadorTicket = possuiMultiplosTickets && indiceInicioTicket === index;
                const ticketSeparador = mostrarSeparadorTicket ? obterTicketInfo(msg.ticket_id) : null;

                return (
                  <React.Fragment key={msg.id}>
                    {mostrarSeparadorTicket && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          my: 0.5,
                          px: 1,
                        }}
                      >
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textAlign: 'center',
                            px: 0.5,
                            lineHeight: 1.45,
                          }}
                        >
                          Atendimento #{msg.ticket_id}
                          {ticketSeparador ? ` • ${getTicketStatusLabel(ticketSeparador.status)}` : ''}
                          {ticketSeparador?.createdAt ? ` • Abertura: ${formatTicketDate(ticketSeparador.createdAt)}` : ''}
                          {ticketSeparador?.data_fechamento
                            ? ` • Fechamento: ${formatTicketDate(ticketSeparador.data_fechamento)}`
                            : ticketSeparador
                              ? ' • Em aberto'
                              : ''}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    )}
                    <MensagemBolha
                      msg={msg}
                      mensagens={mensagens}
                      contatoNome={contatoNome}
                      remetenteLabel={getMensagemRemetenteLabel(msg, contatoNome, user?.id, usuariosPorId)}
                      attachedReactions={reactionsByWid.get(msg.wid) || []}
                      currentUserId={user?.id}
                      usuariosPorId={usuariosPorId}
                    />
                  </React.Fragment>
                );
              })}
            </>
          )}
          <div ref={endRef} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        {ticket && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<OpenInNewIcon />}
            onClick={handleAbrirNaPagina}
          >
            Abrir na página
          </Button>
        )}
        {podeReabrir && (
          <Button
            variant="contained"
            startIcon={reabrindo ? <CircularProgress size={16} color="inherit" /> : <ReopenIcon />}
            onClick={handleReabrir}
            disabled={reabrindo}
          >
            {reabrindo ? 'Reabrindo...' : 'Reabrir atendimento'}
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisualizarTicketDialog;
